// src/utils/analytics.js
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * 1. ユーザープロファイルの統計取得
 *    - 性別
 *    - 年代
 *    - 利用形態（personal / business / education）
 *    - 同行者（family / alone / friends / couple）
 *    regionCode が指定されている場合は、その地域だけを対象に集計
 */
export const getUserProfileStats = async (regionCode = null) => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const stats = {
      totalUsers: 0,
      genderStats: { male: 0, female: 0, other: 0 },
      ageStats: {},
      usageStats: { personal: 0, business: 0, education: 0 },
      companionsStats: { family: 0, alone: 0, friends: 0, couple: 0 },
    };

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();

      // 地域コードでフィルタ（null or 空文字なら全体）
      if (regionCode && data.regionCode !== regionCode) {
        return;
      }

      stats.totalUsers++;

      // 性別
      if (data.gender) {
        stats.genderStats[data.gender] =
          (stats.genderStats[data.gender] || 0) + 1;
      }

      // 年代
      if (data.age) {
        stats.ageStats[data.age] = (stats.ageStats[data.age] || 0) + 1;
      }

      // 利用形態 or 同行者（同じ usage フィールドを値の種類で振り分け）
      if (data.usage) {
        const usageValue = data.usage;

        // 利用形態パターン
        if (['personal', 'business', 'education'].includes(usageValue)) {
          stats.usageStats[usageValue] =
            (stats.usageStats[usageValue] || 0) + 1;
        }

        // 同行者パターン
        if (['family', 'alone', 'friends', 'couple'].includes(usageValue)) {
          stats.companionsStats[usageValue] =
            (stats.companionsStats[usageValue] || 0) + 1;
        }
      }
    });

    return stats;
  } catch (error) {
    console.error('Error getting user profile stats:', error);
    throw error;
  }
};

/**
 * 2. 特定ユーザーのセッション履歴取得
 */
export const getUserSessions = async (userId) => {
  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    const q = query(sessionsRef, orderBy('startTime', 'desc'));
    const snapshot = await getDocs(q);

    const sessions = [];
    for (const sessionDoc of snapshot.docs) {
      const sessionData = { id: sessionDoc.id, ...sessionDoc.data() };

      // セッションの位置情報も取得
      const locationsRef = collection(
        db,
        'users',
        userId,
        'sessions',
        sessionDoc.id,
        'locations'
      );
      const locationsQuery = query(locationsRef, orderBy('timestampId', 'asc'));
      const locationsSnapshot = await getDocs(locationsQuery);

      sessionData.locations = locationsSnapshot.docs.map((locDoc) => ({
        id: locDoc.id,
        ...locDoc.data(),
      }));

      sessions.push(sessionData);
    }

    return sessions;
  } catch (error) {
    console.error('Error getting user sessions:', error);
    throw error;
  }
};

/**
 * 3. 日付範囲でのセッション取得（全ユーザー横断）
 */
export const getSessionsByDateRange = async (startDate, endDate) => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);

    const allSessions = [];

    for (const userDoc of usersSnapshot.docs) {
      const sessionsRef = collection(db, 'users', userDoc.id, 'sessions');
      const q = query(
        sessionsRef,
        where('startTime', '>=', startDate),
        where('startTime', '<=', endDate),
        orderBy('startTime', 'desc')
      );

      const sessionsSnapshot = await getDocs(q);

      sessionsSnapshot.docs.forEach((sessionDoc) => {
        allSessions.push({
          userId: userDoc.id,
          sessionId: sessionDoc.id,
          ...sessionDoc.data(),
        });
      });
    }

    return allSessions;
  } catch (error) {
    console.error('Error getting sessions by date range:', error);
    throw error;
  }
};

/**
 * 4. 位置情報のヒートマップデータ生成（簡易）
 */
export const generateHeatmapData = async (userId = null, sessionId = null) => {
  try {
    const locations = [];

    if (userId && sessionId) {
      // 特定セッションのみ
      const locationsRef = collection(
        db,
        'users',
        userId,
        'sessions',
        sessionId,
        'locations'
      );
      const q = query(locationsRef, orderBy('timestampId', 'asc'));
      const snapshot = await getDocs(q);

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        locations.push({
          lat: data.lat,
          lng: data.lng,
          timestamp: data.timestamp,
          intensity: 1,
        });
      });
    } else {
      // 全体からサンプル
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(usersRef, limit(10)));

      for (const userDoc of usersSnapshot.docs) {
        const sessionsRef = collection(db, 'users', userDoc.id, 'sessions');
        const sessionsSnapshot = await getDocs(query(sessionsRef, limit(5)));

        for (const sessionDoc of sessionsSnapshot.docs) {
          const locationsRef = collection(
            db,
            'users',
            userDoc.id,
            'sessions',
            sessionDoc.id,
            'locations'
          );
          const locationsSnapshot = await getDocs(query(locationsRef, limit(50)));

          locationsSnapshot.docs.forEach((locDoc) => {
            const data = locDoc.data();
            locations.push({
              lat: data.lat,
              lng: data.lng,
              timestamp: data.timestamp,
              userId: userDoc.id,
              sessionId: sessionDoc.id,
              intensity: 1,
            });
          });
        }
      }
    }

    return locations;
  } catch (error) {
    console.error('Error generating heatmap data:', error);
    throw error;
  }
};

/**
 * 5. セッション統計の計算
 */
export const calculateSessionStats = (sessions) => {
  const stats = {
    totalSessions: sessions.length,
    completedSessions: 0,
    averageDuration: 0,
    totalDistance: 0,
    averageDistance: 0,
    locationPointsCount: 0,
  };

  let totalDuration = 0;
  let totalDistance = 0;
  let sessionsWithDuration = 0;

  sessions.forEach((session) => {
    if (session.status === 'completed') {
      stats.completedSessions++;
    }

    if (session.startTime && session.endTime) {
      const duration = session.endTime.seconds - session.startTime.seconds;
      totalDuration += duration;
      sessionsWithDuration++;
    }

    if (session.locations) {
      stats.locationPointsCount += session.locations.length;

      if (session.locations.length > 1) {
        let sessionDistance = 0;
        for (let i = 1; i < session.locations.length; i++) {
          const prev = session.locations[i - 1];
          const curr = session.locations[i];
          sessionDistance += calculateDistance(
            prev.lat,
            prev.lng,
            curr.lat,
            curr.lng
          );
        }
        totalDistance += sessionDistance;
      }
    }
  });

  stats.averageDuration =
    sessionsWithDuration > 0 ? totalDuration / sessionsWithDuration : 0;
  stats.totalDistance = totalDistance;
  stats.averageDistance =
    sessions.length > 0 ? totalDistance / sessions.length : 0;

  return stats;
};

// 距離計算（ハーバーサイン）
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000;
};

/**
 * 6. 個別ユーザーのエクスポート
 */
export const exportUserData = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const sessions = await getUserSessions(userId);

    return {
      user: {
        id: userId,
        ...userData,
      },
      sessions,
      stats: calculateSessionStats(sessions),
    };
  } catch (error) {
    console.error('Error exporting user data:', error);
    throw error;
  }
};

/**
 * 7. activeセッションの最新位置情報を取得
 *    - status が active のセッションのみ対象
 *    - locations の最新1件を取得
 *    - locations がない場合は startLocation を使用
 */
export const getActiveUserLatestLocations = async () => {
  try {
    const activeUsers = [];

    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;

      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const activeSessionsQuery = query(
        sessionsRef,
        where('status', '==', 'active')
      );

      const activeSessionsSnapshot = await getDocs(activeSessionsQuery);

      for (const sessionDoc of activeSessionsSnapshot.docs) {
        const sessionId = sessionDoc.id;
        const sessionData = sessionDoc.data();

        const locationsRef = collection(
          db,
          'users',
          userId,
          'sessions',
          sessionId,
          'locations'
        );

        const latestLocationQuery = query(
          locationsRef,
          orderBy('timestampId', 'desc'),
          limit(1)
        );

        const latestLocationSnapshot = await getDocs(latestLocationQuery);

        if (!latestLocationSnapshot.empty) {
          const latestLocation = latestLocationSnapshot.docs[0].data();

          activeUsers.push({
            userId,
            sessionId,
            lat: latestLocation.lat,
            lng: latestLocation.lng,
            accuracy: latestLocation.accuracy || null,
            timestamp: latestLocation.timestamp || null,
            timestampId: latestLocation.timestampId || null,
            startTime: sessionData.startTime || null,
          });
        } else if (sessionData.startLocation) {
          activeUsers.push({
            userId,
            sessionId,
            lat: sessionData.startLocation.lat,
            lng: sessionData.startLocation.lng,
            accuracy: null,
            timestamp: sessionData.startLocation.timestamp || null,
            timestampId: null,
            startTime: sessionData.startTime || null,
          });
        }
      }
    }

    return activeUsers;
  } catch (error) {
    console.error('Error getting active user latest locations:', error);
    throw error;
  }
};